import type { LiteralUnion } from "type-fest";

import type { Partner } from "./partner";
import type { SalesChecklistMentions } from "./sales";
import type { Citation, Device, ExternalDevice, MultiModal, Tutorial } from "./solve";

export type OrchestratorProducer = "orchestrator-expert-assist-legacy" | "orchestrator";
export type GaiaMessageType = "Search" | "Proactive";
export type GaiaExpertAssistAppName =
    | "expert-assist"
    | "expert-assist-autocomplete"
    | "expert-assist-ext"
    | "expert-assist-autocomplete-ext"
    | "expert-assist-messaging-reactive"
    | "expert-assist-messaging-autocomplete";
export type GaiaExpertAssistExperimentName = "call-context" | "call-context-v2" | "sales-faq";
export type GaiaBotMessage = ReactiveMessagePayload | ProactiveMessagePayload;
export type GaiaApplicationIdentifier = LiteralUnion<"rapport-building", string>;

export interface GaiaPayloadConfigs {
    appName: GaiaExpertAssistAppName;
    areExperimentsEnabled: boolean;
    autoCompleteAppName?: GaiaExpertAssistAppName;
}

// Proactive Message
export type ProactiveMessagePayload = MessageSuggestion;
export interface MessageSuggestion {
    partner: Partner;
    sessionGroupId: string;
    sessionId: string;
    suggestion: {
        message: string;
        messageId: string;
        messageType: GaiaMessageType;
        producer: OrchestratorProducer;
        outputTimestamp: string;
        responseType?: string;
        subflowIdentifier?: string;

        sales_checklist_mentions?: SalesChecklistMentions[] | null;
        category?: string;
        cues?:
            | DeviceCues[]
            | SoftwareCues[]
            | SentimentCues[]
            | DigitalCompetencyCues[]
            | LocationCues[]
            | CallCategoryCues[]
            | null;
        metadata?: { intentClassification: string; applicationIdentifier: GaiaApplicationIdentifier };
        citations?: Citation[] | null;
    };
}

// Reactive Message
export interface OrchestratorInvokeMessagePayload {
    action: "orchestrator-invoke";
    appName: GaiaExpertAssistAppName;
    correlationId: string;
    message: string;
    messageId: string;
    messageType: string;
    partner: Partner;
    sessionGroupId: string;
    sessionId: string;
    userId: string;
    userType: "expert";

    experimentName?: GaiaExpertAssistExperimentName;
    lineOfBusiness?: string;
}
type ReactiveSolveMessage = "reactive-solve" | "reactive-solve-ext";
type ReactiveMessagingSolveMessage = "messaging-reactive-solve";
type ReactiveAutoCompleteMessage = "reactive-autocomplete" | "reactive-autocomplete-ext";
export type ReactiveMessageTypes = ReactiveSolveMessage | ReactiveAutoCompleteMessage | ReactiveMessagingSolveMessage;

export interface ReactiveMessagePayload {
    //required
    messageId: string;
    response: string;

    // optional
    suggestions?: { id: string; text: string }[];
    devices?: Device[] | null;
    externalDevices?: ExternalDevice[] | null;
    tutorials?: Tutorial[] | null;
    multiModal?: MultiModal | null;
    salesChecklistMentions?: SalesChecklistMentions[] | null;
    cues?: DeviceCues[] | SoftwareCues[] | SentimentCues[] | DigitalCompetencyCues[] | null;
    metadata?: {
        intentClassification: string;
        messageType: ReactiveMessageTypes;
    };
    citations?: Citation[] | null;

    // identifiers
    partner: Partner;
    userId: string;
    userType: string;
    sessionGroupId: string;
    sessionId: string;

    // timestamps
    responseTimestamp: string;
    requestTimestamp: string;
}

// Error Message
export interface GaiaErrorResponse {
    name: string;
    correlationId: string;
    body: GaiaErrorMessage;
}

export interface GaiaErrorMessage {
    body: string; // gaia sends this back as a stringified JSON and we need to parse it as GaiaErrorBody
    message: string;
    status: string | number;
    statusText: string;
    trackingId: string;
    responseText: string;
    sessionDetails: {
        sessionId: string;
        sessionGroupId: string;
    };
}

export interface GaiaErrorBody {
    sessionId: string;
    sessionGroupId: string;
    message: string;
    userId: string;
    messageId: string;
    messageType: string;
    userType: string;
}

// Orchestrator Message History
export interface MessageHistoryPayload {
    messages: HistoricMessage[];
    sessionGroupId: string;
    userId: string;
    userType: string;
    inputTimestamp: string;
    outputTimestamp: string;
}

export interface CallTranscriptMessage {
    sessionId: string;
    sessionGroupId: string;
    partner: string;
    metadata: {
        messageType: "proactive";
        responseType: "transcript";
    };
    timestamp: number;
    transcripts?: TranscriptSnippet[];
}

export interface HistoricMessage {
    role: "expert" | "assistant";
    id: string;
    sessionId: string;
    content: string;
    timestamp: string;
}

export interface TranscriptSnippet {
    timestamp: number;
    content: string;
    speaker: "Expert" | "Customer";
}

export interface SentimentCues {
    name: "neutral" | "satisfied" | "frustrated" | "impressed";
    description?: string;
    type: "SENTIMENT";
}

export interface DeviceCues {
    name: string;
    count: number;
    type: "DEVICE";
}

export interface SoftwareCues {
    name: string;
    count: number;
    type: "SOFTWARE";
}

export type DigitalCompetency = "Tech savvy" | "Tech savvy business owner" | "Less tech savvy";
export interface DigitalCompetencyCues {
    name: DigitalCompetency;
    type: "DIGITAL_COMPETENCY";
}

export interface LocationCues {
    name: string;
    type: "LOCATION_STATE" | "LOCATION_CITY";
}

// Shared Call Categories - ex. verizon, at&t
type CallCategories =
    | "APPLICATION/SOFTWARE_TROUBLESHOOTING"
    | "BATTERY"
    | "BILLS_AND_PAYMENT"
    | "CARRIER_ACCOUNT_MANAGEMENT"
    | "CONNECTIVITY_AND_NETWORK"
    | "DEVICE_ACTIVATION"
    | "DEVICE_HARDWARE_TROUBLESHOOTING"
    | "INSURANCE_AND_CLAIMS"
    | "MESSAGING"
    | "NOT_ENOUGH_INFORMATION"
    | "PHONE_CALLS"
    | "SECURITY_AND_PRIVACY"
    | "STORAGE_AND_CONTENT_MANAGEMENT"
    | "VOICEMAIL";

// Call Categories specific to McAfee
type McAfeeCallCategories =
    | "ACCOUNT_MANAGEMENT"
    | "ALTERNATE_LANGUAGE_REQUEST"
    | "FEATURES_AND_BENEFITS"
    | "INSTALLATION_AND_SETUP"
    | "SUBSCRIPTION_MANAGEMENT"
    | "UNINSTALL_AND_REMOVAL"
    | "USAGE_AND_FUNCTIONALITY"
    | "VIRUSES_MALWARE_SPAM"
    | "VPN";

export type CallCategory = CallCategories | McAfeeCallCategories;

export interface CallCategoryCues {
    name: CallCategory;
    type: "CALL_CATEGORY";
}
